import React, { ComponentPropsWithoutRef } from 'react';
import { useFieldArray } from 'react-hook-form';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { faCirclePlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Form from 'components/form';
import { twMerge } from 'tailwind-merge';
import { CampaignCustomField } from 'types/domain';

interface CustomFieldContainerProps extends ComponentPropsWithoutRef<'div'> {}

interface CustomFieldSubmitProps {
    isLoading: boolean;
    children: React.ReactNode;
}

const CustomFieldContainer = (props: CustomFieldContainerProps) => {
    const { className = '', ...rest } = props;

    return <div className={twMerge('flex flex-col gap-6', className)} {...rest} />;
};

const CustomFieldTitleForm = () => {
    const { control, path } = Form.useGivebutterFormContext<CampaignCustomField>();

    return (
        <Form.Container className="flex flex-col gap-1">
            <Form.Label className="text-base" name={path('title')}>
                Title
                <Form.LabelAsterisk />
            </Form.Label>
            <p className="text-sm">What is the title / question of your field?</p>
            <Form.TextInputRoot
                control={control}
                name={path('title')}
                rules={{ required: 'Please enter a title' }}
            >
                <Form.TextInputForm placeholder="T-shirt Size" />
            </Form.TextInputRoot>
            <Form.Error name={path('title')} />
        </Form.Container>
    );
};

const CustomFieldDescriptionForm = () => {
    const { control, path } = Form.useGivebutterFormContext<CampaignCustomField>();

    return (
        <Form.Container className="flex flex-col gap-1">
            <Form.Label className="text-base" name={path('description')}>
                Description
            </Form.Label>
            <p className="text-sm">Add an optional description for your field.</p>
            <Form.Textarea control={control} name={path('description')} rows={8} />
        </Form.Container>
    );
};

const CustomFieldIsRequiredForm = () => {
    const { control, path } = Form.useGivebutterFormContext<CampaignCustomField>();

    return (
        <Form.Container className="flex flex-col gap-1">
            <Form.Label className="text-base" name={path('required')}>
                Required?
            </Form.Label>
            <div className="flex flex-row justify-between">
                <p className="text-sm">Is this field required for all supporters?</p>
                <Form.Switch name={'required'} control={control} />
            </div>
        </Form.Container>
    );
};

const CustomFieldAnswersForm = () => {
    const { control, path, watch } = Form.useGivebutterFormContext<CampaignCustomField>();

    const formAnswers = watch('answers');

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'answers',
    });

    return (
        <Form.Container className="flex flex-col gap-2">
            <Label className="text-sm font-medium">
                Answers
                <Form.LabelAsterisk />
            </Label>

            {fields.map((field, index) => (
                <Form.Container key={field.id} className="flex flex-col gap-2">
                    <Form.Container className="flex items-center gap-4 sm:flex-col md:flex-row">
                        <Form.Container>
                            <Form.Label
                                className="sr-only"
                                name={`${path('answers')}.${index}.value`}
                            >
                                Answer {index}
                            </Form.Label>
                            <Form.TextInputRoot
                                control={control}
                                name={`${path('answers')}.${index}.value`}
                                rules={{ required: 'Please enter an answer' }}
                            >
                                <Form.TextInputForm />
                            </Form.TextInputRoot>
                        </Form.Container>

                        <div className="flex w-full flex-1">
                            <AlertDialog.Root type="warning">
                                <AlertDialog.Trigger asChild={true}>
                                    <Button
                                        variant="solid"
                                        disabled={formAnswers && formAnswers.length === 1}
                                        size="sm"
                                        color="error"
                                        shape={'square'}
                                        aria-label="Delete"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </AlertDialog.Trigger>
                                <AlertDialog.Portal>
                                    <AlertDialog.Overlay />
                                    <AlertDialog.Content>
                                        <AlertDialog.Header>
                                            <AlertDialog.Icon />
                                            <AlertDialog.Close />
                                        </AlertDialog.Header>
                                        <AlertDialog.Body>
                                            <AlertDialog.Title>Delete Answer</AlertDialog.Title>
                                            <AlertDialog.Description>
                                                You are about to delete this custom answer.
                                            </AlertDialog.Description>
                                        </AlertDialog.Body>
                                        <AlertDialog.Footer>
                                            <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                            <AlertDialog.Action onClick={() => remove(index)}>
                                                Confirm
                                            </AlertDialog.Action>
                                        </AlertDialog.Footer>
                                    </AlertDialog.Content>
                                </AlertDialog.Portal>
                            </AlertDialog.Root>
                            <Button
                                size="sm"
                                variant="transparent"
                                shape={'square'}
                                onClick={() => append({ value: '' })}
                                className={`${
                                    formAnswers && formAnswers.length !== index + 1 ? 'hidden' : ''
                                }`}
                                aria-label="Add"
                            >
                                <FontAwesomeIcon icon={faCirclePlus} />
                            </Button>
                        </div>
                    </Form.Container>
                    <Form.Error name={`${path('answers')}.${index}.value`} />
                </Form.Container>
            ))}
        </Form.Container>
    );
};

const CustomFieldSubmitForm = ({ isLoading, children }: CustomFieldSubmitProps) => {
    return (
        <Form.Container className="flex flex-row items-center">
            <Form.SubmitButton
                size="md"
                noAnimation={true}
                isSubmitting={isLoading}
                className="w-auto sm:w-auto"
                variant="solid"
                color="primary"
            >
                Save
            </Form.SubmitButton>

            {children}
        </Form.Container>
    );
};

const Container = CustomFieldContainer;
const Title = CustomFieldTitleForm;
const Description = CustomFieldDescriptionForm;
const IsRequired = CustomFieldIsRequiredForm;
const Answers = CustomFieldAnswersForm;
const Submit = CustomFieldSubmitForm;

export {
    Container,
    Title,
    Description,
    IsRequired,
    Answers,
    Submit,
    //
    CustomFieldContainer,
    CustomFieldTitleForm,
    CustomFieldDescriptionForm,
    CustomFieldIsRequiredForm,
    CustomFieldAnswersForm,
    CustomFieldSubmitForm,
};
